import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isTransparent = transparent && !scrolled;

  const navLinks = [
    { path: '/', label: 'ホーム' },
    { path: '/company', label: '会社概要' },
    { path: '/services', label: '事業紹介' },
    { path: '/contact', label: 'お問い合わせ' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        isTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-10">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            {/* Sci-fi logo icon */}
            <div className="relative w-10 h-10 flex items-center justify-center rounded-md bg-foreground-900 ring-1 ring-amber-400/50 shadow-[0_0_18px_rgba(245,158,11,0.25),inset_0_0_8px_rgba(245,158,11,0.08)] transition-all duration-300 group-hover:shadow-[0_0_28px_rgba(245,158,11,0.4),inset_0_0_14px_rgba(245,158,11,0.15)] group-hover:ring-amber-400/70">
              {/* Scan line animation */}
              <div className="absolute inset-0 rounded-md overflow-hidden pointer-events-none">
                <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-amber-300/60 to-transparent animate-scan"></div>
              </div>
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-[6px] h-[1px] bg-amber-400/80 rounded-full"></div>
              <div className="absolute top-0 right-0 w-[6px] h-[1px] bg-amber-400/80 rounded-full"></div>
              <div className="absolute bottom-0 left-0 w-[6px] h-[1px] bg-amber-400/80 rounded-full"></div>
              <div className="absolute bottom-0 right-0 w-[6px] h-[1px] bg-amber-400/80 rounded-full"></div>
              {/* 3D Text */}
              <span className="relative z-10 font-bold text-xs tracking-[0.15em] text-amber-400" style={{ fontFamily: "'Orbitron', sans-serif" }}>
                3D
              </span>
            </div>
            {/* Sci-fi brand name */}
            <div className="flex flex-col">
              <span className={`font-heading text-lg font-semibold tracking-[0.15em] whitespace-nowrap leading-tight transition-colors ${
                isTransparent ? 'text-foreground-50' : 'text-foreground-900'
              }`}>
                3D's Card Data
              </span>
              <span className={`text-[9px] tracking-[0.25em] uppercase whitespace-nowrap leading-none transition-colors ${
                isTransparent ? 'text-foreground-300/80' : 'text-foreground-500'
              }`}>
                Data Intelligence
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative px-5 py-2 text-sm font-medium tracking-wide whitespace-nowrap transition-all duration-300 rounded-full ${
                  location.pathname === link.path
                    ? isTransparent
                      ? 'text-foreground-50 bg-foreground-900/20'
                      : 'text-primary-600 bg-primary-100/70'
                    : isTransparent
                      ? 'text-foreground-200 hover:text-foreground-50 hover:bg-foreground-900/10'
                      : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-200'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
              isTransparent ? 'text-foreground-50 hover:bg-foreground-900/10' : 'text-foreground-700 hover:bg-background-200'
            }`}
            aria-label="メニュー"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className={`px-4 py-3 text-sm font-medium tracking-wide rounded-lg transition-colors ${
                  location.pathname === link.path
                    ? 'text-primary-600 bg-primary-100/70'
                    : 'text-foreground-600 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}